import {
	Card,
	createStyles,
	Divider,
	Group,
	Stack,
	Tooltip,
} from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { AISetting, CatalogRule } from '@repo/api-codegen';
import {
	useApiCreateAiOutputFeedback,
	useApiGetAutomationDailyAiOutputRemaining,
	useApiPreviewBulkAiGeneration,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { useMobxReaction } from '@repo/common/utils/useMobxReaction';
import {
	Badge,
	Icon,
	IconButton,
	ResourceIcon,
	Switch,
	Text,
} from '@repo/foundations';
import { buttonHoverTransition } from '@repo/theme/primitives/transitions';
import { isNil } from 'lodash-es';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useState } from 'react';
import { useAuthUser } from '../../api';
import { useBackgroundJob } from '../../api/hooks/backgroundJob';
import { useCatalogList } from '../../api/hooks/resourceCatalog';
import { useTagList } from '../../api/hooks/tag';
import { BackgroundJob } from '../../lib/models';
import { useAutomationV2Store } from '../../pages/AutomationPage/V2/useAutomationV2Store';
import { ANALYTICS_EVENTS } from '../../utils/constants/analytics';
import { AddAiSettingModal } from './components/AddAiSettingModal';
import { AiSetting } from './components/AiSetting';
import { AiOutputBlockStore } from './store';
import { isValidPreviewData } from './types';

interface AiOutputBlockControllerInternalProps {
	store: AiOutputBlockStore;
	includeTitle?: boolean;
	onAiOutputBlockDelete: () => void;
	tooltip?: string;
}

const useStyles = createStyles((theme) => ({
	switchGroup: {
		display: 'flex',
		justifyContent: 'space-between',
		flexWrap: 'nowrap',
	},
	titleGroup: {
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		'&:not(:hover) .right-component-hover': {
			opacity: 0,
		},
		'&:hover .right-component-hover': {
			opacity: 1,
			...buttonHoverTransition('opacity'),
		},
		borderTopLeftRadius: theme.radius.md,
		borderTopRightRadius: theme.radius.md,
	},
	titleGroupContent: {
		gap: theme.spacing.sm,
	},
	switchGroupContent: {
		gap: 0,
	},
	stack: {
		gap: 0,
	},
}));

const AiOutputBlockControllerInternal = observer(
	({
		store,
		onAiOutputBlockDelete,
		includeTitle = true,
		tooltip,
	}: AiOutputBlockControllerInternalProps) => {
		const { classes } = useStyles();
		const automationStore = useAutomationV2Store();
		const { user, workspace } = useAuthUser();
		const [opened, { open, close }] = useDisclosure(false);
		const [isReview, setIsReview] = useState(false);
		const [debouncedSearch] = useDebouncedValue(store.searchTerm, 100);

		const { data: dailyAIOutputRemaining } =
			useApiGetAutomationDailyAiOutputRemaining(
				{
					pathParams: {
						automationId: automationStore.automation?.id ?? '',
					},
				},
				{
					enabled: !isNil(automationStore.automation?.id),
				}
			);

		const { mutateAsync: submitFeedback, isPending: isSubmittingFeedback } =
			useApiCreateAiOutputFeedback();

		const { data: resources } = useCatalogList({
			filters: {
				filter: store.filterRule,
				search_term: debouncedSearch,
				grab_all_entity_types: true,
			},
		});

		const setPreviewIndexWithBounds = useCallback(
			(index: number) => {
				const currentAiSettingStore = store.getCurrentAiSettingStore();
				const resourcesToPreview =
					currentAiSettingStore?.aiSettingAction.resources_to_preview;
				const resourceCount = resourcesToPreview?.length ?? 0;

				if (resourceCount === 0) {
					store.setPreviewIndex(0);
					store.setCurrentPreviewItem(undefined);
					store.setCurrentResource(undefined);
				} else {
					const newIndex = Math.max(0, Math.min(index, resourceCount - 1));
					store.setPreviewIndex(newIndex);

					// Update the current preview item and resource based on the new index
					if (store.previewData && resourcesToPreview && resources) {
						const resourceId = resourcesToPreview[newIndex];
						const item = store.previewData[resourceId];
						const resource = resources.results.find((r) => r.id === resourceId);

						store.setCurrentPreviewItem(item);
						store.setCurrentResource(resource);
					} else {
						store.setCurrentPreviewItem(undefined);
						store.setCurrentResource(undefined);
					}
				}
			},
			[store, resources]
		);

		const previewMutation = useApiPreviewBulkAiGeneration({
			onMutate: () => {
				store.setIsPreviewLoading(true);
				store.setPreviewData(undefined);
				store.setPreviewError(null);
			},
			onSuccess: (response) => {
				// Response should contain the job ID
				const jobId = (response as { background_job?: { id?: string } })
					?.background_job?.id;
				if (jobId) {
					store.setPreviewJobId(jobId);
				} else {
					store.setIsPreviewLoading(false);

					// Track preview failed event if no job ID
					trackEvent(
						ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_FAILED,
						{
							automation_id: automationStore.automation?.id || '',
							error_reason: 'no_job_id',
						},
						user,
						workspace
					);
				}
			},
			onError: () => {
				store.setIsPreviewLoading(false);

				// Track preview failed event
				trackEvent(
					ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_FAILED,
					{
						automation_id: automationStore.automation?.id || '',
						error_reason: 'mutation_error',
					},
					user,
					workspace
				);
			},
		});

		// Function to fetch artifact content
		const fetchArtifactContent = useCallback(
			async (jobId: string) => {
				try {
					const job = new BackgroundJob({ id: jobId });
					const artifactContent = await job.getArtifactContent();

					// Safely cast and validate the data
					if (isValidPreviewData(artifactContent)) {
						store.setPreviewData(artifactContent);

						// Set the current preview item and resource based on the current index
						const currentAiSettingStore = store.getCurrentAiSettingStore();
						const resourcesToPreview =
							currentAiSettingStore?.aiSettingAction.resources_to_preview;

						if (
							resourcesToPreview &&
							resourcesToPreview.length > 0 &&
							resources
						) {
							const resourceId = resourcesToPreview[store.previewIndex];
							const item = artifactContent[resourceId];
							const resource = resources.results.find(
								(r) => r.id === resourceId
							);

							store.setCurrentPreviewItem(item);
							store.setCurrentResource(resource);
						}

						// Track preview completed successfully
						trackEvent(
							ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_COMPLETED,
							{
								automation_id: automationStore.automation?.id || '',
								preview_results_count:
									Object.keys(artifactContent).length.toString(),
							},
							user,
							workspace
						);
					} else {
						store.setPreviewError({ detail: 'Preview data format is invalid' });

						// Track preview failed due to invalid data format
						trackEvent(
							ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_FAILED,
							{
								automation_id: automationStore.automation?.id || '',
								error_reason: 'invalid_data_format',
							},
							user,
							workspace
						);
					}

					store.setIsPreviewLoading(false);
				} catch {
					store.setPreviewError({ detail: 'Failed to fetch preview results' });
					store.setIsPreviewLoading(false);

					// Track preview failed due to fetch error
					trackEvent(
						ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_FAILED,
						{
							automation_id: automationStore.automation?.id || '',
							error_reason: 'fetch_error',
						},
						user,
						workspace
					);
				}
			},
			[store, resources, automationStore.automation?.id, user, workspace]
		);

		// Poll the background job until completion
		useBackgroundJob({
			id: store.previewJobId || '',
			options: {
				enabled: !!store.previewJobId,
				refetchInterval: (query) => {
					const { data } = query.state;

					if (data?.failed) {
						store.setIsPreviewLoading(false);
						store.setPreviewError({ detail: 'Preview generation job failed' });
						store.setPreviewJobId(undefined);
						return false;
					}

					if (data?.completed) {
						// Job completed successfully, fetch artifact content
						if (data.has_artifact && store.previewJobId) {
							fetchArtifactContent(store.previewJobId);
						} else {
							store.setIsPreviewLoading(false);
							store.setPreviewError({
								detail: 'Job completed but no artifact was generated',
							});
						}
						store.setPreviewJobId(undefined);
						return false;
					}

					// Continue polling every second while job is running
					return 1000;
				},
			},
		});

		const handleAddAiSetting = useCallback(() => {
			const newAiSettingId = store.addAiSetting();
			setIsReview(false);
			open();
			store.setCurrentAiSettingId(newAiSettingId);

			// Track AI Output Block added event
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_BLOCK_ADDED,
				{
					automation_id: automationStore.automation?.id || '',
					ai_settings_count: (store.aiSettings.length + 1).toString(),
				},
				user,
				workspace
			);
		}, [open, store, automationStore.automation?.id, user, workspace]);

		const handleModalClose = useCallback(() => {
			close();
			if (store.currentAiSettingId) {
				store.deleteAiSetting(store.currentAiSettingId);
			}
			store.resetModalState();
			setPreviewIndexWithBounds(0);
			previewMutation.reset(); // Reset mutation state when modal closes
		}, [close, store, previewMutation, setPreviewIndexWithBounds]);

		const { data: tagsData } = useTagList({});

		const handleModalComplete = useCallback(() => {
			store.updateAiSettings();
			close();
			store.setCurrentAiSettingId(undefined);

			// Track AI Output Block configured event
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_BLOCK_CONFIGURED,
				{
					automation_id: automationStore.automation?.id || '',
					ai_settings_count: store.aiSettings.length.toString(),
				},
				user,
				workspace
			);
		}, [close, store, automationStore.automation?.id, user, workspace]);

		const handlePreview = useCallback(() => {
			if (!store.currentAiSettingId || !automationStore.automation?.id) {
				return;
			}

			const aiSettingStore = store.getCurrentAiSettingStore();
			if (!aiSettingStore?.aiSettingAction) {
				return;
			}

			const aiSetting = aiSettingStore.aiSettingAction;

			// Track AI generation preview started event
			trackEvent(
				ANALYTICS_EVENTS.AI_GENERATION_PREVIEW_STARTED,
				{
					automation_id: automationStore.automation.id,
					ai_setting_type: aiSetting.type || '',
					preview_resources_count:
						aiSetting.resources_to_preview?.length?.toString() || '0',
				},
				user,
				workspace
			);

			previewMutation.mutate({
				pathParams: {
					automationId: automationStore.automation.id,
				},
				body: aiSetting as AISetting,
			});
		}, [
			automationStore.automation?.id,
			store,
			previewMutation,
			user,
			workspace,
		]);

		const handleFeedbackSubmit = useCallback(
			async (sentiment: 'positive' | 'negative') => {
				const { currentResource, currentPreviewItem, currentAiSettingId } =
					store;
				const automationId = automationStore.automation?.id;
				const aiSettingAction =
					store.getCurrentAiSettingStore()?.aiSettingAction;

				if (
					!currentResource ||
					!currentPreviewItem ||
					!currentAiSettingId ||
					!automationId ||
					!aiSettingAction
				) {
					return;
				}

				try {
					await submitFeedback({
						body: {
							automation_id: automationId,
							ai_setting_id: currentAiSettingId,
							entity_id: currentResource.id,
							prompt_config: {
								type: aiSettingAction.type,
								prompt: aiSettingAction.prompt,
								bind_pii: aiSettingAction.bind_pii,
								bind_tags: aiSettingAction.bind_tags,
								bind_owners: aiSettingAction.bind_owners,
								bind_description: aiSettingAction.bind_description,
								bind_definition: aiSettingAction.bind_definition,
							},
							ai_output: currentPreviewItem,
							feedback: { sentiment, timestamp: new Date().toISOString() },
						},
					});

					store.setCurrentFeedback(sentiment);
					showNotification({
						title: 'Feedback received',
						message: 'Thank you for your feedback!',
						color: 'green',
					});
				} catch (error) {
					showNotification({
						title: 'Failed to submit feedback',
						message: `Please try again later. ${error instanceof Error ? error.message : ''}`,
						color: 'red',
					});
				}
			},
			[automationStore.automation?.id, submitFeedback, store]
		);

		return (
			<>
				<Card withBorder radius="md">
					{includeTitle && (
						<Card.Section withBorder={store.hasAiSettings}>
							<Group position="apart" p="md" className={classes.titleGroup}>
								<Group className={classes.titleGroupContent}>
									<ResourceIcon
										iconName="sparkles"
										backgroundColor="fill/ai-secondary/default"
									/>
									<Text size="sm" weight="semibold">
										Generate AI output
									</Text>
									<Badge>Beta</Badge>
								</Group>
								<Group spacing="3xs">
									{dailyAIOutputRemaining?.remaining === 0 && (
										<Tooltip label="Daily AI output limit reached">
											<Icon
												name="infoCircleFilled"
												color="icon/warning/default"
											/>
										</Tooltip>
									)}
									<IconButton
										iconName="plus"
										onClick={handleAddAiSetting}
										variant="tertiary"
										tooltip={
											store.filterRule.operands.length === 0
												? 'Add filters above to enable AI output'
												: 'Add rule'
										}
										disabled={store.filterRule.operands.length === 0}
										showTooltipWhenDisabled
										tooltipProps={{ withinPortal: true }}
									/>
									<IconButton
										iconName="x"
										onClick={onAiOutputBlockDelete}
										variant="tertiary"
										tooltip={tooltip ?? 'Remove action'}
										className="right-component-hover"
										includeOffset
										tooltipProps={{ withinPortal: true }}
									/>
								</Group>
							</Group>
						</Card.Section>
					)}
					{store.hasAiSettings && (
						<Card.Section p={0}>
							<Stack className={classes.stack}>
								{Array.from(store.aiSettingsMap.keys()).map(
									(storedAiSettingId, index) => {
										const aiSettingStore =
											store.aiSettingsMap.get(storedAiSettingId);

										if (!aiSettingStore) return null;

										return (
											<>
												{index > 0 && <Divider mx="md" />}
												<AiSetting
													key={`ai-setting-${storedAiSettingId}`}
													store={aiSettingStore}
													onDelete={() =>
														store.deleteAiSetting(storedAiSettingId)
													}
													onClick={() => {
														setIsReview(true);
														open();
														store.setCurrentAiSettingId(storedAiSettingId);
													}}
												/>
											</>
										);
									}
								)}
							</Stack>
						</Card.Section>
					)}
					{store.filterRule.operands.length > 0 && (
						<Card.Section p="md" withBorder>
							<Group className={classes.switchGroup}>
								<Group className={classes.switchGroupContent}>
									<Text size="xs">
										Review AI generated output before it publishes
									</Text>
									<Text
										size="xs"
										variant="tertiary"
										color="text/secondary/default"
									>
										AI Outputs will not be applied until they are reviewed and
										manually published.
									</Text>
								</Group>
								<Switch
									checked={store.enableAIOutputReview}
									onChange={(event) => {
										const reviewEnabled = event.currentTarget.checked;
										store.setEnableAIOutputReview(reviewEnabled);

										// Track AI Output Block review toggle event
										trackEvent(
											ANALYTICS_EVENTS.AI_OUTPUT_BLOCK_REVIEW_TOGGLED,
											{
												automation_id: automationStore.automation?.id || '',
												review_enabled: reviewEnabled.toString(),
											},
											user,
											workspace
										);
									}}
								/>
							</Group>
						</Card.Section>
					)}
				</Card>
				{opened &&
					store.currentAiSettingId &&
					automationStore.automation?.id && (
						<AddAiSettingModal
							store={store}
							onComplete={handleModalComplete}
							onClose={handleModalClose}
							isReview={isReview}
							onReviewClose={() => {
								close();
								store.resetPreviewState();
								setPreviewIndexWithBounds(0);
								previewMutation.reset();
							}}
							resources={resources}
							onPreview={handlePreview}
							setPreviewIndex={setPreviewIndexWithBounds}
							onFeedbackSubmit={handleFeedbackSubmit}
							isSubmittingFeedback={isSubmittingFeedback}
							tagsData={tagsData}
						/>
					)}
			</>
		);
	}
);

interface AiOutputBlockControllerProps
	extends Pick<
		AiOutputBlockControllerInternalProps,
		'onAiOutputBlockDelete' | 'tooltip'
	> {
	onAiSettingsChange: (edit: AISetting[]) => void;
	initialAiSettings: AISetting[];
	filterRule: CatalogRule;
	enableAIOutputReview: boolean;
	onEnableAIOutputReviewChange: (enableAIOutputReview: boolean) => void;
}

export const AiOutputBlockController = observer(
	({
		onAiSettingsChange,
		initialAiSettings,
		filterRule,
		enableAIOutputReview,
		onEnableAIOutputReviewChange,
		...rest
	}: AiOutputBlockControllerProps) => {
		const [store] = useState(
			() =>
				new AiOutputBlockStore(
					initialAiSettings,
					filterRule,
					enableAIOutputReview
				)
		);

		useMobxReaction(
			useCallback(() => store.aiSettings, [store]),
			useCallback(
				(editSettings) => {
					onAiSettingsChange(toJS(editSettings));
				},
				[onAiSettingsChange]
			)
		);

		useMobxReaction(
			useCallback(() => store.enableAIOutputReview, [store]),
			useCallback(
				(newEnableAIOutputReview) => {
					onEnableAIOutputReviewChange(newEnableAIOutputReview);
				},
				[onEnableAIOutputReviewChange]
			)
		);

		return <AiOutputBlockControllerInternal {...rest} store={store} />;
	}
);
